/**
 * Multi-seleção sobre um catálogo em memória.
 *
 * É a peça dos filtros: escolhe vários componentes, várias cores, vários
 * chicotes. Não confundir com o **campo de busca com criar** do lançamento, que
 * é outro contrato — aquele escolhe um só, aceita valor novo e resolve por
 * descrição. Este aqui só filtra o que já existe.
 *
 * O filtro digitado casa **por palavra, sem caixa e sem acento** ("rele bomba"
 * acha "Relé da bomba"). Com o catálogo inteiro em memória ele é local; quando
 * o catálogo passa da página e oferece `buscar`, o termo vai ao servidor —
 * dois caracteres e 400 ms de espera, a regra 2 — porque filtrar só a primeira
 * página escondia exatamente o que ficou de fora dela.
 *
 * O topo da lista oferece sempre **selecionar todos** os que estão à vista:
 * com termo digitado, os encontrados — digitar "01" e marcar os cinco pinos que
 * começam com 01 é um clique, não cinco; sem termo, a lista inteira, mesmo
 * depois de já ter marcado um.
 *
 * O rodapé separa as duas contagens: quantos existem (no projeto, ou com os
 * outros filtros) e quantos casam com o que foi digitado.
 *
 * O id é número nos cadastros e texto quando o próprio valor é o filtro (pino,
 * observação, fonte).
 */

import { useEffect, useMemo, useRef, useState } from "react"
import * as Menu from "@radix-ui/react-dropdown-menu"
import { useQuery } from "@tanstack/react-query"
import type { Catalogo, ItemDeCatalogo } from "@/api/catalogos"
import { BUSCA_MINIMO_CARACTERES } from "@/api/query"
import { casaBusca } from "./buscaDeTexto"
import { escalaFlutuante } from "./flutuante"

export function Multi<K extends number | string = number>({
  rotulo, catalogo, escolhidos, aoMudar, largura = 150, aoAbrir,
}: {
  rotulo: string
  catalogo: Catalogo<K>
  /** ids escolhidos */
  escolhidos: K[]
  aoMudar: (ids: K[]) => void
  largura?: number
  /** Avisa quando o menu abre e fecha — para quem só carrega a lista com alguém olhando. */
  aoAbrir?: (aberto: boolean) => void
}) {
  const [termo, setTermo] = useState("")
  const campo = useRef<HTMLInputElement>(null)

  // O termo que vai ao servidor espera a digitação parar.
  const [termoServidor, setTermoServidor] = useState("")
  useEffect(() => {
    const t = setTimeout(() => setTermoServidor(termo.trim()), 400)
    return () => clearTimeout(t)
  }, [termo])
  const noServidor = !catalogo.completo && catalogo.buscar != null
    && termoServidor.length >= BUSCA_MINIMO_CARACTERES
  const doServidor = useQuery({
    queryKey: [...(catalogo.chave ?? ["multi-busca", rotulo]), "busca", catalogo.total, termoServidor] as const,
    enabled: noServidor,
    staleTime: 60_000,
    queryFn: () => catalogo.buscar!(termoServidor),
  })

  // Quem já apareceu fica conhecido: o escolhido pela busca no servidor pode não
  // estar na primeira página, e o botão ainda precisa saber o nome dele.
  const conhecidos = useRef(new Map<K, ItemDeCatalogo<K>>())
  for (const i of catalogo.itens) conhecidos.current.set(i.id, i)
  for (const i of doServidor.data ?? []) conhecidos.current.set(i.id, i)

  const filtrados = useMemo(() => {
    if (noServidor && doServidor.data) return doServidor.data
    if (!termo.trim()) return catalogo.itens
    return catalogo.itens.filter((i) => casaBusca(termo, i.rotulo, i.codigo))
  }, [catalogo.itens, termo, noServidor, doServidor.data])

  const escolhidosSet = new Set(escolhidos)
  const primeiro = escolhidos.map((id) => conhecidos.current.get(id)).find(Boolean)

  // Quando o valor é o próprio texto, ele já é o nome — mesmo antes de a lista carregar.
  const sozinho = escolhidos[0]
  const resumo =
    escolhidos.length === 0 ? rotulo
    : escolhidos.length === 1
      ? (primeiro?.codigo || primeiro?.rotulo
         || (typeof sozinho === "string" ? (sozinho || "(vazio)") : "1 escolhido"))
    : `${rotulo} · ${escolhidos.length}`

  function alternar(id: K) {
    aoMudar(escolhidosSet.has(id) ? escolhidos.filter((x) => x !== id) : [...escolhidos, id])
  }

  // "Selecionar todos": o que a busca trouxe, de uma vez — somado ao que já estava marcado.
  // Uma chamada só a `aoMudar`, então o filtro aplica uma vez, não N.
  const todosVisiveisMarcados = filtrados.length > 0 && filtrados.every((i) => escolhidosSet.has(i.id))
  function marcarVisiveis() {
    if (todosVisiveisMarcados) {
      const sair = new Set(filtrados.map((i) => i.id))
      aoMudar(escolhidos.filter((x) => !sair.has(x)))
    } else {
      aoMudar([...escolhidos, ...filtrados.map((i) => i.id).filter((id) => !escolhidosSet.has(id))])
    }
  }

  const ativo = escolhidos.length > 0

  // O rodapé: o total vem antes do texto digitado, e diz de onde vem.
  const digitado = termo.trim()
  const base = catalogo.chave ? "com os outros filtros" : "no projeto"

  return (
    <Menu.Root onOpenChange={(a) => {
      aoAbrir?.(a)
      if (a) setTimeout(() => campo.current?.focus(), 10); else setTermo("")
    }}>
      <Menu.Trigger
        style={{
          display: "inline-flex", alignItems: "center", gap: 6, height: 28, maxWidth: largura,
          padding: "0 9px", borderRadius: 7, fontSize: 12,
          background: ativo ? "var(--brand-soft)" : "var(--surface)",
          color: ativo ? "var(--brand-on-soft)" : "var(--txt2)",
          border: `1px solid ${ativo ? "var(--brand)" : "var(--line2)"}`,
          fontWeight: ativo ? 600 : 400,
        }}
      >
        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {resumo}
        </span>
        <svg width="11" height="11" viewBox="0 0 24 24" aria-hidden="true"
             style={{ flex: "0 0 11px", fill: "none", stroke: "currentColor", strokeWidth: 2.4, strokeLinecap: "round", strokeLinejoin: "round" }}>
          <path d="m6 9.5 6 6 6-6" />
        </svg>
      </Menu.Trigger>

      <Menu.Portal>
        <Menu.Content
          sideOffset={5} align="start"
          onCloseAutoFocus={(e) => e.preventDefault()}
          style={{
            width: 280, padding: 7, borderRadius: 10, background: "var(--surface)",
            border: "1px solid var(--line2)", boxShadow: "var(--sombra)", color: "var(--txt)", zIndex: 60,
            ...escalaFlutuante,
          }}
        >
          <input
            ref={campo}
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            placeholder={`Filtrar ${rotulo.toLowerCase()}…`}
            aria-label={`Filtrar ${rotulo}`}
            style={{
              width: "100%", height: 28, padding: "0 9px", marginBottom: 6, borderRadius: 7,
              border: "1px solid var(--line2)", background: "var(--surface2)", fontSize: 12.5,
            }}
          />

          {filtrados.length > 1 && (
            <button
              onClick={marcarVisiveis}
              style={{
                display: "flex", alignItems: "center", gap: 8, width: "100%", marginBottom: 4,
                padding: "5px 7px", borderRadius: 6, fontSize: 12, fontWeight: 600, textAlign: "left",
                color: "var(--brand-on-soft)", background: "var(--brand-soft)",
              }}
            >
              <Caixa marcada={todosVisiveisMarcados} />
              {todosVisiveisMarcados
                ? `Desmarcar todos (${filtrados.length})`
                : `Selecionar todos (${filtrados.length})`}
            </button>
          )}

          <div style={{ maxHeight: 260, overflowY: "auto", display: "flex", flexDirection: "column", gap: 1 }}>
            {(catalogo.carregando || (noServidor && doServidor.isFetching)) && <Aviso>Carregando…</Aviso>}
            {/* Falha não é lista vazia: "nada encontrado" mandaria a pessoa mudar o filtro à toa. */}
            {(catalogo.erro || (noServidor && doServidor.isError))
              ? <Aviso>Não foi possível carregar as opções. Feche e abra de novo.</Aviso>
              : !catalogo.carregando && filtrados.length === 0 && <Aviso>Nada encontrado.</Aviso>}

            {filtrados.map((i) => {
              const marcado = escolhidosSet.has(i.id)
              return (
                <button
                  key={String(i.id)}
                  onClick={() => alternar(i.id)}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, width: "100%",
                    padding: "5px 7px", borderRadius: 6, fontSize: 12.5, textAlign: "left",
                    background: marcado ? "var(--brand-soft)" : "transparent",
                    color: marcado ? "var(--brand-on-soft)" : "var(--txt)",
                  }}
                >
                  <Caixa marcada={marcado} />
                  {i.codigo && (
                    <span style={{ fontFamily: "var(--fm)", fontWeight: 600, flex: "0 0 auto" }}>{i.codigo}</span>
                  )}
                  <span style={{
                    flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    fontStyle: !i.rotulo && !i.codigo ? "italic" : undefined,
                    color: !i.rotulo && !i.codigo ? "var(--txt3)" : undefined,
                  }}>
                    {i.rotulo || (i.codigo ? "" : "(vazio)")}
                  </span>
                  {i.usos > 0 && (
                    <span style={{ fontFamily: "var(--fm)", fontSize: 10.5, color: "var(--txt3)", flex: "0 0 auto" }}>
                      {i.usos}
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          <div style={{
            display: "flex", alignItems: "center", gap: 8, marginTop: 7, paddingTop: 6,
            borderTop: "1px solid var(--line)", fontSize: 11, color: "var(--txt3)",
          }}>
            {/* Quando o catálogo não cabe na página, dizer — em vez de deixar a
                pessoa achar que aqueles são todos os itens que existem. */}
            <span style={{ flex: 1 }}>
              {catalogo.completo
                ? (digitado ? `${filtrados.length} com "${digitado}" · ` : "") + `${catalogo.total} ${base}`
                : noServidor && doServidor.data
                  ? `${doServidor.data.length} com "${termoServidor}" · ${catalogo.total} ${base}`
                  : catalogo.buscar
                    ? `mostrando ${catalogo.itens.length} de ${catalogo.total} — digite para buscar em todos`
                    : `mostrando ${catalogo.itens.length} de ${catalogo.total} — refine pelo campo acima`}
            </span>
            {escolhidos.length > 0 && (
              <button onClick={() => aoMudar([])} style={{ fontSize: 11, color: "var(--brand-on-soft)", fontWeight: 600 }}>
                limpar
              </button>
            )}
          </div>
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  )
}

function Caixa({ marcada }: { marcada: boolean }) {
  return (
    <span
      aria-hidden="true"
      style={{
        flex: "0 0 14px", width: 14, height: 14, borderRadius: 4,
        border: `1.5px solid ${marcada ? "var(--brand)" : "var(--line2)"}`,
        background: marcada ? "var(--brand)" : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      {marcada && (
        <svg width="10" height="10" viewBox="0 0 24 24"
             style={{ fill: "none", stroke: "#FFFFFF", strokeWidth: 3.4, strokeLinecap: "round", strokeLinejoin: "round" }}>
          <path d="m5 12.5 4.5 4.5L19 7.5" />
        </svg>
      )}
    </span>
  )
}

function Aviso({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: "14px 8px", fontSize: 12, color: "var(--txt3)", textAlign: "center" }}>{children}</div>
}
